import { Box, Checkbox, Flex, Grid, Spinner } from '@chakra-ui/react';
import { FC } from 'react';

import BoolCell from '@app/components/common/table/cells/bool';

import { ApplyDistributorRow } from '../../utils/apply-helpers';
import { SectionCard } from './section-card';

const StatusBadge = ({ status }: { status: ApplyDistributorRow['changeStatus'] }) => {
  if (status === 'added') {
    return (
      <Box
        as="span"
        fontSize="11.5px"
        color="#2f7d33"
        bg="#d8f2d8"
        border="1px solid #79c079"
        px="10px"
        py="3px"
        borderRadius="20px"
        whiteSpace="nowrap"
      >
        Добавлен
      </Box>
    );
  }

  if (status === 'modified') {
    return (
      <Box
        as="span"
        fontSize="11.5px"
        color="#2f7d33"
        bg="#d8f2d8"
        border="1px solid #79c079"
        px="10px"
        py="3px"
        borderRadius="20px"
        whiteSpace="nowrap"
      >
        Изменён
      </Box>
    );
  }

  if (status === 'removed') {
    return (
      <Box
        as="span"
        fontSize="11.5px"
        color="#c0392b"
        bg="#fdecea"
        border="1px solid #e6a8a0"
        px="10px"
        py="3px"
        borderRadius="20px"
        whiteSpace="nowrap"
      >
        Удалён
      </Box>
    );
  }

  return (
    <Box
      as="span"
      fontSize="11.5px"
      color="#8a94a3"
      bg="#f0f2f5"
      border="1px solid #e2e6ec"
      px="10px"
      py="3px"
      borderRadius="20px"
      whiteSpace="nowrap"
    >
      Без изменений
    </Box>
  );
};

const HeaderCell = ({
  children,
  textAlign,
}: {
  children: string;
  textAlign?: 'center' | 'right';
}) => (
  <Box
    fontSize="11.5px"
    fontWeight={700}
    letterSpacing=".03em"
    color="#98a2b0"
    textTransform="uppercase"
    textAlign={textAlign}
    lineHeight={1.3}
  >
    {children}
  </Box>
);

type ApplyDistributorsSectionProps = {
  rows: ApplyDistributorRow[];
  isLoading: boolean;
  hasChanges: boolean;
  selected: Record<number, boolean>;
  onToggle: (distributorId: number) => void;
  readOnly?: boolean;
};

export const ApplyDistributorsSection: FC<ApplyDistributorsSectionProps> = ({
  rows,
  isLoading,
  hasChanges,
  selected,
  onToggle,
  readOnly = false,
}) => (
  <SectionCard
    title="Список дистрибьюторов"
    subtitle={hasChanges
      ? (readOnly
        ? 'В заявке изменён состав дистрибьюторов.'
        : 'В заявке изменён состав дистрибьюторов. Отметьте строки, изменения которых нужно применить.')
      : undefined}
  >
    <Box overflowX="auto">
      <Grid
        templateColumns={readOnly
          ? 'minmax(150px, 1.5fr) 118px 78px 74px 108px'
          : '30px minmax(150px, 1.5fr) 118px 78px 74px 108px'}
        alignItems="end"
        gap={2.5}
        px={6}
        pb={2}
        minW="600px"
      >
        {!readOnly && <Box />}
        <HeaderCell>Дистрибьютор</HeaderCell>
        <HeaderCell>ID ТТ в системе дистрибьютора</HeaderCell>
        <HeaderCell textAlign="center">Основной</HeaderCell>
        <HeaderCell textAlign="center">Активен</HeaderCell>
        <HeaderCell textAlign="right">Статус</HeaderCell>
      </Grid>

      {isLoading ? (
        <Flex justifyContent="center" py={6}>
          <Spinner />
        </Flex>
      ) : rows.length === 0 ? (
        <Box px={6} py={4} borderTop="1px solid #eef1f5" fontSize="14px" color="#8a94a3">
          Нет данных
        </Box>
      ) : (
        rows.map((row) => {
          const isChanged = row.selectable;
          const textColor = isChanged ? '#1e4620' : '#3a4150';
          const fontWeight = isChanged ? 500 : 400;

          const content = (
            <>
              {!readOnly && (
                <Box textAlign="center">
                  {isChanged ? (
                    <Checkbox
                      isChecked={!!selected[row.distributor_id]}
                      onChange={() => onToggle(row.distributor_id)}
                      colorScheme="blue"
                      size="md"
                    />
                  ) : (
                    <Box color="#c2c9d2" fontSize="15px">—</Box>
                  )}
                </Box>
              )}
              <Box fontSize="14px" color={textColor} fontWeight={fontWeight} wordBreak="break-all">
                {row.distributor_name} [{row.distributor_id}]
              </Box>
              <Box fontSize="14px" color={textColor} fontWeight={fontWeight}>
                {row.distributor_outlet_id || '—'}
              </Box>
              <Flex justifyContent="center">
                <BoolCell value={row.is_general} />
              </Flex>
              <Flex justifyContent="center">
                <BoolCell value={row.is_active} />
              </Flex>
              <Box textAlign="right">
                <StatusBadge status={row.changeStatus} />
              </Box>
            </>
          );

          if (isChanged) {
            return (
              <Grid
                key={row.distributor_id}
                as={readOnly ? 'div' : 'label'}
                templateColumns={readOnly
                  ? 'minmax(150px, 1.5fr) 118px 78px 74px 108px'
                  : '30px minmax(150px, 1.5fr) 118px 78px 74px 108px'}
                alignItems="center"
                gap={2.5}
                px={6}
                py={4}
                borderTop="1px solid #eef1f5"
                bg="#eafaea"
                cursor={readOnly ? undefined : 'pointer'}
                minW="600px"
              >
                {content}
              </Grid>
            );
          }

          return (
            <Grid
              key={row.distributor_id}
              templateColumns={readOnly
                ? 'minmax(150px, 1.5fr) 118px 78px 74px 108px'
                : '30px minmax(150px, 1.5fr) 118px 78px 74px 108px'}
              alignItems="center"
              gap={2.5}
              px={6}
              py={4}
              borderTop="1px solid #eef1f5"
              minW="600px"
            >
              {content}
            </Grid>
          );
        })
      )}
    </Box>
  </SectionCard>
);
